/**
 * Empreinte visuelle F&L via le bridge local (Apple FeaturePrint, port 3334).
 * 100% local : l'image ne sort jamais sur le réseau, seul le vecteur (~Ko) circule.
 * Utilisé pour la reconnaissance "qui apprend" (plus-proche-voisin sur l'index local).
 */
import { logger } from '../../utils/logger';
import { enTetesBridgeJson } from '../bridgeHeaders';

const BRIDGE_URL = 'https://localhost:3334';
const EMBED_TIMEOUT = 6000;

/** base64 (Float32 brut) → Float32Array */
export function b64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return new Float32Array(u8.buffer);
}

/** Float32Array → base64 (Float32 brut) */
export function float32ToB64(v: Float32Array): string {
  const u8 = new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

/**
 * Calcule l'empreinte d'une image (base64 JPEG) via le bridge.
 * Retourne null si le bridge est injoignable (ex. iPhone sans bridge).
 */
export async function embedFrame(imageBase64: string): Promise<Float32Array | null> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), EMBED_TIMEOUT);
  try {
    const r = await fetch(`${BRIDGE_URL}/produce-embed`, {
      method: 'POST',
      headers: enTetesBridgeJson(),
      body: JSON.stringify({ image: imageBase64 }),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) return null;
    const d = await r.json();
    if (!d.embedding) return null;
    return b64ToFloat32(d.embedding);
  } catch (err: any) {
    clearTimeout(t);
    logger.log('[ProduceEmbed] bridge injoignable:', err?.message);
    return null;
  }
}

// ── Dernière capture (pour l'apprentissage auto sur encodage F&L) ───────────────
// La reconnaissance mémorise l'empreinte de la dernière pesée ; quand le vendeur
// encode un F&L juste après, on l'apprend (empreinte → produit).
let lastCapture: { vec: Float32Array; ts: number } | null = null;

export function rememberCapture(vec: Float32Array): void {
  lastCapture = { vec, ts: Date.now() };
}

/**
 * Récupère ET consomme la dernière capture si elle est récente (one-shot).
 * Fenêtre large (30 s) : le vendeur pose le F&L sur la balance (capture de la BONNE image),
 * peut le retirer, naviguer/chercher, puis sélectionner le produit — l'image reste valable.
 */
export function takeRecentCapture(maxAgeMs = 30000): Float32Array | null {
  if (lastCapture && Date.now() - lastCapture.ts <= maxAgeMs) {
    const v = lastCapture.vec;
    lastCapture = null;
    return v;
  }
  return null;
}
